import { computed } from 'mobx'
import { dateFormatters } from '@/utils/date-util'
import { type JobNote } from '@/features/jobs/details/notes/JobNote'
import type { Id } from '@/infra/id'
import { memberRoleLabel } from '@/features/jobs/details/member-role-label'

/**
 * A note in a job's notes list.
 */
export class JobNoteItemViewModel {
  constructor(
    private readonly _note: JobNote,
    private readonly _getMentionLabel: (userPublicId: Id) => string | undefined,
  ) {}

  /**
   * The note's ID.
   */
  @computed
  get id() {
    return this._note.id
  }

  /**
   * The note's author.
   */
  @computed
  get author() {
    return this._note.author
  }

  /**
   * The author's display name.
   */
  @computed
  get displayName() {
    return this.author.displayName
  }

  /**
   * The author's role on the job, if it has a label.
   */
  @computed
  get role(): string | undefined {
    return memberRoleLabel(this.author)
  }

  /**
   * When the note was written, as "Today, <time>" or a full date.
   */
  @computed
  get formattedTime() {
    const date = this._note.dateCreated
    const now = new Date()
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()

    if (isToday) {
      const time = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
      return `Today, ${time}`
    }

    return dateFormatters.dateLongWithTime.format(date)
  }

  /**
   * The note's text.
   */
  @computed
  get body() {
    return this._note.body
  }

  /**
   * Labels of the users the note notified.
   */
  @computed
  get mentions(): string[] {
    return this._note.recipientUserPublicIds
      .map((id) => this._getMentionLabel(id))
      .filter((label): label is string => !!label)
  }
}
